"use client";

import clsx from "clsx";
import { useTranslations } from "@/lib/compat/i18n";

/**
 * AttributionStrip — single horizontal glass strip pinned to the top of
 * every action screen. Shows "Actioned by: <party>" on the left and
 * "Notified: <party> · <party>" on the right so the audience always
 * understands the responsibility flow on a given screen.
 *
 *   Used for: active picker actions, sign/issue/accept CTAs, and
 *   automatic emissions (carrier milestones, customs clearances,
 *   settlement events).
 *
 *   Driven by lib/attribution/attributions.ts — single source of truth
 *   keyed by screen ID. SceneRouter looks the row up and renders the
 *   strip above the scene body. Screens with no meaningful action
 *   return `null` from getAttribution → no strip rendered.
 */

export type PartyRole =
  | "Seller"
  | "Buyer"
  | "Bank"
  | "Carrier"
  | "Insurer"
  | "Authority"
  | "Rail"
  | "Platform";

export type Party = {
  /** Display name shown in the chip — e.g. "Aurora", "Al Madar". */
  label: string;
  /** Role tag rendered after the label in parentheses — e.g. "(Seller)". */
  role: PartyRole;
  /** Optional country flag (ISO-2 code) — drives the small flag glyph. */
  flag?: string;
  /** Optional secondary label, e.g. bank name "FAB" or carrier brand. */
  sublabel?: string;
};

export type Attribution = {
  actor: Party;
  notified: Party[];
};

const ROLE_STYLES: Record<PartyRole, string> = {
  Seller:    "border-state-verified/45 bg-state-verified/10 text-state-verified",
  Buyer:     "border-state-financed/45 bg-state-financed/10 text-state-financed",
  Bank:      "border-state-financed/60 bg-state-financed/15 text-state-financed",
  Carrier:   "border-state-traced/45 bg-state-traced/10 text-state-traced",
  Insurer:   "border-state-cold/45 bg-state-cold/10 text-state-cold",
  Authority: "border-state-traced/60 bg-state-traced/15 text-state-traced",
  Rail:      "border-orbitra-teal-soft/45 bg-orbitra-teal-soft/10 text-orbitra-teal-soft",
  Platform:  "border-orbitra-teal-soft/45 bg-orbitra-teal-soft/10 text-orbitra-teal-soft",
};

export function AttributionStrip({
  attribution,
  className,
}: {
  attribution: Attribution;
  className?: string;
}) {
  const { actor, notified } = attribution;
  const t = useTranslations("attribution");
  return (
    <div
      className={clsx(
        "glass mb-3 flex items-center justify-between gap-3 px-4 py-2 text-[11px]",
        className,
      )}
      data-attribution
    >
      <div className="flex items-center gap-2">
        <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-muted">
          {t("actionedBy")}
        </span>
        <PartyChip party={actor} />
      </div>
      <div className="flex items-center gap-2">
        <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-muted">
          {t("notified")}
        </span>
        <div className="flex flex-wrap items-center gap-1.5">
          {notified.length === 0 ? (
            <span className="text-orbitra-muted">—</span>
          ) : (
            notified.map((p, i) => <PartyChip key={`${p.label}-${i}`} party={p} />)
          )}
        </div>
      </div>
    </div>
  );
}

function PartyChip({ party }: { party: Party }) {
  return (
    <span
      className={clsx(
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-wider",
        ROLE_STYLES[party.role],
      )}
    >
      {party.flag && (
        <span className="font-mono text-[9px] opacity-75">{party.flag}</span>
      )}
      <span className="font-semibold">{party.label}</span>
      <span className="opacity-70">· {party.role}</span>
      {party.sublabel && <span className="opacity-70">· {party.sublabel}</span>}
    </span>
  );
}
